<template>
  <SoDialog
    v-model="value"
    :card-title="reward ? 'Edit prize' : 'Add prize'"
    :max-width="480"
    data-testid="prize-form-dialog"
  >
    <SoTextField
      v-model="form.name"
      label="Name*"
      :rules="[rules.required]"
      data-testid="prize-name-field"
    />
    <SoSelect
      v-model="form.type"
      :items="typeOptions"
      label="Type*"
      class="mt-2"
      data-testid="prize-type-select"
    />
    <SoNumberField
      v-model="form.quantity"
      label="Quantity (number of winners)*"
      :min="1"
      :precision="0"
      class="mt-2"
      data-testid="prize-quantity-field"
    />
    <!-- No :min — it would clamp the null→0 coerced value up to the min on open,
         pre-filling £0.01; positivity is enforced by isFormValid. -->
    <SoNumberField
      v-if="typeHasCreditValue"
      v-model="form.valuePounds"
      label="Prize value (£)*"
      :precision="2"
      class="mt-2"
      data-testid="prize-value-field"
    />
    <template v-if="typeHasFuelValues">
      <SoNumberField
        v-model="form.singleFuelPounds"
        label="Single fuel value (£)*"
        :precision="2"
        class="mt-2"
        data-testid="prize-single-value-field"
      />
      <SoNumberField
        v-model="form.dualFuelPounds"
        label="Dual fuel value (£)*"
        :precision="2"
        class="mt-2"
        data-testid="prize-dual-value-field"
      />
    </template>
    <div class="d-flex justify-end mt-4">
      <SoButton title="Cancel" class="mr-2" large @click="value = false" />
      <SoButton
        :title="reward ? 'Save' : 'Add prize'"
        type="success"
        large
        :loading="isPending"
        :disabled="!isFormValid || isPending"
        data-testid="prize-save-btn"
        @click="save"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoNumberField from "@/components/formControls/SoNumberField.vue"
  import rules from "@/validators/rules"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { DrawRewardTypeEnum } from "@/api/backofficeRest/generated/model"
  import { IDrawReward } from "@/models/operations/draw/drawReward.types"
  import { IDrawRewardInput } from "@/mutators/loyalty/LoyaltyMutator.types"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import {
    REWARD_TYPE_LABELS,
    EDITABLE_REWARD_TYPES,
    rewardTypeHasCreditValue,
    rewardTypeHasFuelValues,
  } from "./drawRewardTypeConfig"

  const penceToPounds = (pence: number | null): number | null =>
    pence === null ? null : pence / 100

  const poundsToPence = (pounds: number | null): number | null =>
    pounds === null ? null : Math.round(pounds * 100)

  export default defineComponent({
    components: { SoDialog, SoButton, SoTextField, SoSelect, SoNumberField },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      drawId: {
        type: String,
        required: true,
      },
      reward: {
        type: Object as PropType<IDrawReward | null>,
        default: null,
      },
    },
    emits: ["update:modelValue", "saved"],
    data() {
      return {
        rules,
        snackbar: useSnackbarStore(),
        form: {
          name: "",
          type: DrawRewardTypeEnum.ACCOUNT_CREDIT as DrawRewardTypeEnum,
          quantity: 1,
          valuePounds: null as number | null,
          singleFuelPounds: null as number | null,
          dualFuelPounds: null as number | null,
        },
        createMutation: LoyaltyMutator.createReward(),
        updateMutation: LoyaltyMutator.updateReward(),
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(newValue: boolean) {
          this.$emit("update:modelValue", newValue)
        },
      },
      typeOptions(): { title: string; value: DrawRewardTypeEnum }[] {
        return EDITABLE_REWARD_TYPES.map((type) => ({
          title: REWARD_TYPE_LABELS[type] ?? type,
          value: type,
        }))
      },
      isPending(): boolean {
        return this.createMutation.isPending || this.updateMutation.isPending
      },
      typeHasCreditValue(): boolean {
        return rewardTypeHasCreditValue(this.form.type)
      },
      typeHasFuelValues(): boolean {
        return rewardTypeHasFuelValues(this.form.type)
      },
      isFormValid(): boolean {
        if (!this.form.name.trim()) return false
        if (this.form.quantity < 1) return false
        if (this.typeHasCreditValue) {
          return !!this.form.valuePounds && this.form.valuePounds > 0
        }
        if (this.typeHasFuelValues) {
          return (
            !!this.form.singleFuelPounds &&
            this.form.singleFuelPounds > 0 &&
            !!this.form.dualFuelPounds &&
            this.form.dualFuelPounds > 0
          )
        }
        return true
      },
    },
    watch: {
      modelValue(open: boolean) {
        if (open) this.populateForm()
      },
    },
    methods: {
      populateForm() {
        this.form = this.reward
          ? {
              name: this.reward.name,
              type: this.reward.type,
              quantity: this.reward.quantity,
              valuePounds: penceToPounds(this.reward.creditValuePence),
              singleFuelPounds: penceToPounds(this.reward.singleFuelPence),
              dualFuelPounds: penceToPounds(this.reward.dualFuelPence),
            }
          : {
              name: "",
              type: DrawRewardTypeEnum.ACCOUNT_CREDIT,
              quantity: 1,
              valuePounds: null,
              singleFuelPounds: null,
              dualFuelPounds: null,
            }
      },
      async save() {
        if (!this.isFormValid) return
        const reward: IDrawRewardInput = {
          name: this.form.name.trim(),
          type: this.form.type,
          quantity: this.form.quantity,
          creditValuePence: this.typeHasCreditValue
            ? poundsToPence(this.form.valuePounds)
            : null,
          singleFuelPence: this.typeHasFuelValues
            ? poundsToPence(this.form.singleFuelPounds)
            : null,
          dualFuelPence: this.typeHasFuelValues
            ? poundsToPence(this.form.dualFuelPounds)
            : null,
        }
        try {
          await (this.reward
            ? this.updateMutation.mutateAsync({
                drawId: this.drawId,
                rewardId: this.reward.id,
                reward,
              })
            : this.createMutation.mutateAsync({ drawId: this.drawId, reward }))
        } catch (error) {
          errorHandler.showCaughtError(error, "Failed to save prize")
          return
        }
        this.snackbar.addSuccess(this.reward ? "Prize updated" : "Prize added")
        this.$emit("saved")
        this.value = false
      },
    },
  })
</script>
